import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardBody } from '@/components/common/Card';
import Button from '@/components/common/Button';
import { useAuthStore } from '@/stores/authStore';
import toast from 'react-hot-toast';
import {
  getLeadBillingSettings,
  getLeadPricingConfigs,
  getLeadPricingAudit,
  getLeadBillingStats,
  updatePlatformSetting,
  updateLeadPricingConfig,
  type PlatformSetting,
  type LeadPricingConfig,
  type LeadPricingAudit,
  type LeadBillingStats,
} from '@/services/leadBillingApi';

export default function LeadBilling() {
  const { hasPermission } = useAuthStore();
  const queryClient = useQueryClient();
  const canWrite = hasPermission('settings', 'write');

  const [activeTab, setActiveTab] = useState<'settings' | 'pricing' | 'audit' | 'stats'>('settings');
  const [editingPriceId, setEditingPriceId] = useState<string | null>(null);
  const [editPriceCents, setEditPriceCents] = useState<number>(0);

  // Queries
  const { data: settings, isLoading: loadingSettings } = useQuery({
    queryKey: ['leadBillingSettings'],
    queryFn: getLeadBillingSettings,
  });

  const { data: pricing, isLoading: loadingPricing } = useQuery({
    queryKey: ['leadPricingConfigs'],
    queryFn: getLeadPricingConfigs,
  });

  const { data: auditLog, isLoading: loadingAudit } = useQuery({
    queryKey: ['leadPricingAudit'],
    queryFn: () => getLeadPricingAudit(50),
    enabled: activeTab === 'audit',
  });

  const { data: stats, isLoading: loadingStats } = useQuery({
    queryKey: ['leadBillingStats'],
    queryFn: () => getLeadBillingStats(),
    enabled: activeTab === 'stats',
  });

  // Mutations
  const updateSettingMutation = useMutation({
    mutationFn: ({ key, value }: { key: string; value: any }) => updatePlatformSetting(key, value),
    onSuccess: () => {
      toast.success('Setting updated');
      queryClient.invalidateQueries({ queryKey: ['leadBillingSettings'] });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to update setting');
    },
  });

  const updatePricingMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<LeadPricingConfig> }) =>
      updateLeadPricingConfig(id, updates),
    onSuccess: () => {
      toast.success('Pricing updated');
      queryClient.invalidateQueries({ queryKey: ['leadPricingConfigs'] });
      queryClient.invalidateQueries({ queryKey: ['leadPricingAudit'] });
      setEditingPriceId(null);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to update pricing');
    },
  });

  // Helper to get setting value
  const getSettingValue = (key: string): any => {
    const setting = settings?.find((s) => s.key === key);
    return setting?.value;
  };

  // Format cents to dollars
  const formatCents = (cents: number): string => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  // Parse JSON array safely
  const parseJsonArray = (value: any): any[] => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch {
        return [];
      }
    }
    return [];
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Lead Billing</h1>
        <p className="mt-1 text-sm text-gray-500">
          Configure lead pricing, budgets, and billing settings
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'settings', label: 'Global Settings' },
            { id: 'pricing', label: 'Pricing' },
            { id: 'audit', label: 'Audit Log' },
            { id: 'stats', label: 'Statistics' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-brand text-brand'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          {/* Enable/Disable Lead Billing */}
          <Card>
            <CardHeader>
              <h2 className="text-lg font-semibold">Lead Billing System</h2>
            </CardHeader>
            <CardBody>
              {loadingSettings ? (
                <div className="flex justify-center py-4">
                  <div className="w-6 h-6 border-2 border-brand border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Master Switch */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <h3 className="font-medium">Enable Lead Billing</h3>
                      <p className="text-sm text-gray-500">
                        Master switch to enable/disable the entire lead billing system
                      </p>
                    </div>
                    {canWrite && (
                      <button
                        onClick={() => {
                          const current = getSettingValue('lead_billing_enabled');
                          const newValue = !(current === 'true' || current === true);
                          updateSettingMutation.mutate({
                            key: 'lead_billing_enabled',
                            value: newValue,
                          });
                        }}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          getSettingValue('lead_billing_enabled') === 'true' ||
                          getSettingValue('lead_billing_enabled') === true
                            ? 'bg-brand'
                            : 'bg-gray-300'
                        }`}
                        aria-label="Toggle lead billing system"
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            getSettingValue('lead_billing_enabled') === 'true' ||
                            getSettingValue('lead_billing_enabled') === true
                              ? 'translate-x-6'
                              : 'translate-x-1'
                          }`}
                        />
                      </button>
                    )}
                  </div>

                  {/* Budget Settings */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 border rounded-lg">
                      <p className="block text-sm font-medium text-gray-700">
                        Minimum Monthly Budget
                      </p>
                      <div className="mt-1 flex items-center gap-2">
                        <span className="text-lg font-semibold">
                          {formatCents(parseInt(getSettingValue('lead_billing_minimum_budget_cents')) || 5000)}
                        </span>
                        {canWrite && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              const newValue = prompt(
                                'Enter minimum budget in cents:',
                                String(getSettingValue('lead_billing_minimum_budget_cents') || 5000)
                              );
                              if (newValue) {
                                updateSettingMutation.mutate({
                                  key: 'lead_billing_minimum_budget_cents',
                                  value: newValue,
                                });
                              }
                            }}
                          >
                            Edit
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="p-4 border rounded-lg">
                      <p className="block text-sm font-medium text-gray-700">
                        Default Budget for New Businesses
                      </p>
                      <div className="mt-1 flex items-center gap-2">
                        <span className="text-lg font-semibold">
                          {formatCents(parseInt(getSettingValue('lead_billing_default_budget_cents')) || 5000)}
                        </span>
                        {canWrite && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              const newValue = prompt(
                                'Enter default budget in cents:',
                                String(getSettingValue('lead_billing_default_budget_cents') || 5000)
                              );
                              if (newValue) {
                                updateSettingMutation.mutate({
                                  key: 'lead_billing_default_budget_cents',
                                  value: newValue,
                                });
                              }
                            }}
                          >
                            Edit
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="p-4 border rounded-lg">
                      <p className="block text-sm font-medium text-gray-700">
                        Budget Warning Threshold
                      </p>
                      <div className="mt-1 flex items-center gap-2">
                        <span className="text-lg font-semibold">
                          {getSettingValue('lead_billing_budget_warning_default_percent') || 80}%
                        </span>
                        {canWrite && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              const newValue = prompt(
                                'Enter warning threshold percentage:',
                                String(getSettingValue('lead_billing_budget_warning_default_percent') || 80)
                              );
                              if (newValue) {
                                updateSettingMutation.mutate({
                                  key: 'lead_billing_budget_warning_default_percent',
                                  value: newValue,
                                });
                              }
                            }}
                          >
                            Edit
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="p-4 border rounded-lg">
                      <p className="block text-sm font-medium text-gray-700">
                        Lead Dedup Window (Rolling)
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        Same consumer = 1 lead within this window
                      </p>
                      <div className="mt-1 flex items-center gap-2">
                        <span className="text-lg font-semibold">
                          {getSettingValue('lead_billing_dedup_window_days_default') || 365} days
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Dedup Window Options */}
                  <div className="p-4 border rounded-lg">
                    <p className="block text-sm font-medium text-gray-700 mb-2">
                      Available Deduplication Window Options
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {parseJsonArray(getSettingValue('lead_billing_dedup_window_options')).map((days: number) => (
                        <span
                          key={days}
                          className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                        >
                          {days} days
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Grace Period Options */}
                  <div className="p-4 border rounded-lg">
                    <p className="block text-sm font-medium text-gray-700 mb-2">
                      Available Grace Period Options
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {parseJsonArray(getSettingValue('lead_billing_grace_period_options')).map((hours: number) => (
                        <span
                          key={hours}
                          className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                        >
                          {hours === 0 ? 'Immediate' : `${hours} hrs`}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Free Tier Toggle */}
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <h3 className="font-medium">Free Tier Gets Leads</h3>
                      <p className="text-sm text-gray-500">
                        Allow free tier businesses to receive billable leads
                      </p>
                    </div>
                    {canWrite && (
                      <button
                        onClick={() => {
                          const current = getSettingValue('lead_billing_free_tier_gets_leads');
                          const newValue = !(current === 'true' || current === true);
                          updateSettingMutation.mutate({
                            key: 'lead_billing_free_tier_gets_leads',
                            value: newValue,
                          });
                        }}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          getSettingValue('lead_billing_free_tier_gets_leads') === 'true' ||
                          getSettingValue('lead_billing_free_tier_gets_leads') === true
                            ? 'bg-brand'
                            : 'bg-gray-300'
                        }`}
                        aria-label="Toggle free tier lead eligibility"
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            getSettingValue('lead_billing_free_tier_gets_leads') === 'true' ||
                            getSettingValue('lead_billing_free_tier_gets_leads') === true
                              ? 'translate-x-6'
                              : 'translate-x-1'
                          }`}
                        />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      )}

      {/* Pricing Tab */}
      {activeTab === 'pricing' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Lead Pricing by Contact Method</h2>
              <p className="text-sm text-gray-500">
                Billing: One lead per consumer within rolling dedup window
              </p>
            </div>
          </CardHeader>
          <CardBody>
            {loadingPricing ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Contact Method
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Tier
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Price
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Billable
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Active
                      </th>
                      {canWrite && (
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {pricing?.map((config) => (
                      <tr key={config.id}>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="font-medium">{config.display_name || config.contact_method}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs bg-gray-100 rounded">
                            {config.subscription_tier}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {editingPriceId === config.id ? (
                            <input
                              type="number"
                              value={editPriceCents}
                              onChange={(e) => setEditPriceCents(parseInt(e.target.value) || 0)}
                              className="w-24 px-2 py-1 border rounded text-sm"
                              placeholder="Cents"
                              aria-label={`Price in cents for ${config.display_name || config.contact_method}`}
                            />
                          ) : (
                            <span className="font-semibold text-green-600">
                              {formatCents(config.price_cents)}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 text-xs rounded ${
                              config.is_billable
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {config.is_billable ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 text-xs rounded ${
                              config.is_active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {config.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        {canWrite && (
                          <td className="px-4 py-3 whitespace-nowrap">
                            {editingPriceId === config.id ? (
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() =>
                                    updatePricingMutation.mutate({
                                      id: config.id,
                                      updates: { price_cents: editPriceCents },
                                    })
                                  }
                                  loading={updatePricingMutation.isPending}
                                >
                                  Save
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setEditingPriceId(null)}
                                >
                                  Cancel
                                </Button>
                              </div>
                            ) : (
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setEditingPriceId(config.id);
                                    setEditPriceCents(config.price_cents);
                                  }}
                                >
                                  Edit Price
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() =>
                                    updatePricingMutation.mutate({
                                      id: config.id,
                                      updates: { is_billable: !config.is_billable },
                                    })
                                  }
                                >
                                  Toggle Billable
                                </Button>
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* Audit Log Tab */}
      {activeTab === 'audit' && (
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold">Pricing Change Audit Log</h2>
          </CardHeader>
          <CardBody>
            {loadingAudit ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin" />
              </div>
            ) : auditLog && auditLog.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Contact Method
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Tier
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Old Price
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        New Price
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Reason
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {auditLog.map((entry) => (
                      <tr key={entry.id}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          {new Date(entry.created_at).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {entry.contact_method}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {entry.subscription_tier}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {entry.old_price_cents !== null ? formatCents(entry.old_price_cents) : '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {entry.new_price_cents !== null ? formatCents(entry.new_price_cents) : '-'}
                        </td>
                        <td className="px-4 py-3">
                          {entry.reason || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No pricing changes recorded yet</p>
            )}
          </CardBody>
        </Card>
      )}

      {/* Statistics Tab */}
      {activeTab === 'stats' && (
        <div className="space-y-6">
          {loadingStats ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin" />
            </div>
          ) : stats ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardBody>
                    <p className="text-sm text-gray-500">Total Leads This Month</p>
                    <p className="text-3xl font-bold">{stats.totalLeads}</p>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody>
                    <p className="text-sm text-gray-500">Total Revenue</p>
                    <p className="text-3xl font-bold text-green-600">
                      {formatCents(stats.totalRevenue)}
                    </p>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody>
                    <p className="text-sm text-gray-500">Average Lead Value</p>
                    <p className="text-3xl font-bold">
                      {formatCents(Math.round(stats.averageLeadValue))}
                    </p>
                  </CardBody>
                </Card>
              </div>

              {/* Leads by Contact Method */}
              <Card>
                <CardHeader>
                  <h2 className="text-lg font-semibold">Leads by Contact Method</h2>
                </CardHeader>
                <CardBody>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(stats.leadsByMethod).map(([method, count]) => (
                      <div key={method} className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500 capitalize">{method.replace('_', ' ')}</p>
                        <p className="text-2xl font-bold">{count}</p>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>

              {/* Leads by Status */}
              <Card>
                <CardHeader>
                  <h2 className="text-lg font-semibold">Leads by Status</h2>
                </CardHeader>
                <CardBody>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(stats.leadsByStatus).map(([status, count]) => (
                      <div key={status} className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500 capitalize">{status.replace('_', ' ')}</p>
                        <p className="text-2xl font-bold">{count}</p>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>

              {/* Top Businesses */}
              <Card>
                <CardHeader>
                  <h2 className="text-lg font-semibold">Top Businesses by Lead Spend</h2>
                </CardHeader>
                <CardBody>
                  {stats.topBusinesses.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead>
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Business
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Leads
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Total Spent
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {stats.topBusinesses.map((biz) => (
                            <tr key={biz.business_id}>
                              <td className="px-4 py-3">{biz.business_name}</td>
                              <td className="px-4 py-3">{biz.lead_count}</td>
                              <td className="px-4 py-3 font-semibold text-green-600">
                                {formatCents(biz.total_spent)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No lead data available</p>
                  )}
                </CardBody>
              </Card>
            </>
          ) : (
            <p className="text-gray-500 text-center py-8">Failed to load statistics</p>
          )}
        </div>
      )}
    </div>
  );
}
